import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, ImageOverlay, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// ---------------------------------------------------------------------------
// 24-hour forecast tab constants
// ---------------------------------------------------------------------------
const FORECAST_24H_URL = "/data/lai/lai_24h_latest.png";
// Clean overlay image (no UI elements, transparent background, exact bounds)
// for use with L.imageOverlay() on the live lightning map.
const OVERLAY_URL = "/data/lai/lai_overlay_latest.png";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const FORECAST_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48];
const MS_PER_HOUR    = 3600 * 1000;
const INDEX_URL  = "/data/lai/index.json";
const RUNS_URL   = "/data/lai/runs";

// Individual colour for each LAI value 1–10
const LAI_COLORS = {
  1:  "#A8CFB6",
  2:  "#BFD27F",
  3:  "#D6D957",
  4:  "#E9D85A",
  5:  "#F2BF42",
  6:  "#F89C27",
  7:  "#F06A1E",
  8:  "#F33D14",
  9:  "#E02A2A",
  10: "#B45AA9",
};

// Abbreviated weekday and full month name arrays for valid-time formatting
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS   = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Legacy category fallback colours (for older data files that use range strings)
const LEGACY_INTENSITY_COLORS = {
  "1-3":   "#A8CFB6",
  "4-6":   "#F2BF42",
  "7-9":   "#F33D14",
  "10-10": "#B45AA9",
};

// UK & Ireland centre and bounding box (SW corner, NE corner)
const MAP_CENTER = [54.5, -4.0];
const MAP_ZOOM   = 6;
// Bounds that encompass the UK and Ireland, including northern Scotland
const UK_BOUNDS  = [[49.5, -10.5], [61.0, 2.5]];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Return the fill colour for a given integer LAI value (1–10).
 * Values outside this range fall back to the lowest colour.
 */
function getLAIColor(value) {
  const v = Number(value);
  return LAI_COLORS[v] ?? "#A8CFB6";
}

function layerStyle(feature) {
  const props = feature?.properties ?? {};
  let color;
  if (props.lai_value != null) {
    // New data format: numeric lai_value property
    color = getLAIColor(props.lai_value);
  } else if (props.lai != null) {
    // Alternative field name used in some dataset versions
    color = getLAIColor(props.lai);
  } else if (props.intensity != null && !isNaN(Number(props.intensity))) {
    // Intermediate format: intensity stored as numeric string ("1"–"10")
    color = getLAIColor(Number(props.intensity));
  } else {
    // Legacy format: intensity stored as range string ("1-3", "4-6", …)
    color = LEGACY_INTENSITY_COLORS[props.intensity] ?? "#A8CFB6";
  }
  return {
    color,
    weight:      1.5,
    opacity:     0.9,
    fillColor:   color,
    fillOpacity: 0.35,
    smoothFactor: 2.0,
  };
}

function onEachFeature(feature, layer) {
  const { forecast_hour, lai_decimal, lai_value, intensity } = feature.properties ?? {};
  // Prefer lai_decimal (new LAI.v2 field), fall back to lai_value / intensity
  const rawValue = lai_decimal ?? lai_value ?? intensity ?? "—";
  // Show the actual numeric LAI value (including decimals if present)
  const displayValue = rawValue !== "—" ? String(parseFloat(Number(rawValue).toFixed(2))) : "—";
  layer.bindTooltip(
    `<strong>LAI ${displayValue}</strong><br/>` +
    `<strong>T+${forecast_hour}</strong>`,
    { sticky: true }
  );
}

/**
 * Return the ordinal suffix for a given day-of-month number.
 */
function ordinalSuffix(day) {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:  return "st";
    case 2:  return "nd";
    case 3:  return "rd";
    default: return "th";
  }
}

/**
 * Parse a run ID such as "20260309_12z" and return its UTC start time in ms.
 */
function getRunTimeMs(runId) {
  if (!runId) return null;
  const match = runId.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})z$/);
  if (!match) return null;
  const [, yyyy, mm, dd, hh] = match;
  return Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh));
}

/**
 * Format a run ID such as "20260309_12z" into a human-readable label.
 * Returns "12z 09/03/26".
 */
function formatRunLabel(runId) {
  if (!runId) return "";
  // runId = "YYYYMMDD_HHz"
  const match = runId.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})z$/);
  if (!match) return runId;
  const [, yyyy, mm, dd, hh] = match;
  const yy = yyyy.slice(2);
  return `${hh}z ${dd}/${mm}/${yy}`;
}

/**
 * Calculate the forecast valid time from a run ID and a forecast step (hours).
 * Returns a string like "Thu 12th March, 12:00 UTC".
 */
function formatValidTime(runId, forecastHour) {
  if (!runId) return "—";
  const runMs = getRunTimeMs(runId);
  if (runMs === null) return "—";
  const validMs = runMs + forecastHour * MS_PER_HOUR;
  const d = new Date(validMs);
  const H       = String(d.getUTCHours()).padStart(2, "0");
  const M       = String(d.getUTCMinutes()).padStart(2, "0");
  const day     = d.getUTCDate();
  const weekday = WEEKDAYS[d.getUTCDay()];
  const month   = MONTHS[d.getUTCMonth()];
  return `${weekday} ${day}${ordinalSuffix(day)} ${month}, ${H}:${M} UTC`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LAI 24-Hour Forecast tab component
// ---------------------------------------------------------------------------
function LAI24HForecastTab() {
  const [imgStatus, setImgStatus] = useState("loading"); // "loading" | "ok" | "error"

  // Derive a human-readable "last generated" label from today's date.
  // The pipeline runs daily at 09:00 UTC, so the image covers today's
  // 06Z → tomorrow's 06Z window (if run today) or the previous day otherwise.
  const generatedLabel = (() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, "0");
    const d = String(now.getUTCDate()).padStart(2, "0");
    return `Generated daily at 09:00 UTC · Issue date ${d}/${m}/${String(y).slice(2)}`;
  })();

  return (
    <div className="space-y-4">
      {/* Timestamp bar */}
      {imgStatus === "ok" && (
        <p className="text-xs text-[#6B7280]">{generatedLabel}</p>
      )}

      {/* Image display */}
      <div
        className="rounded-lg overflow-hidden border border-white/10 bg-[#111827] flex items-center justify-center"
        style={{ minHeight: "400px" }}
      >
        {imgStatus === "loading" && (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-8 h-8 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[#9CA3AF]">Loading 24-hour forecast…</p>
          </div>
        )}
        {imgStatus === "error" && (
          <div className="text-center space-y-2 p-8">
            <p className="text-sm text-red-400">24-hour forecast image not yet available.</p>
            <p className="text-xs text-[#6B7280]">
              The forecast is generated daily at 09:00 UTC. Please check back later.
            </p>
          </div>
        )}
        <img
          src={FORECAST_24H_URL}
          alt="24-hour LAI.v1 lightning risk forecast for the UK"
          className={`w-full h-auto rounded-lg ${imgStatus !== "ok" ? "hidden" : ""}`}
          onLoad={() => setImgStatus("ok")}
          onError={() => setImgStatus("error")}
        />
      </div>

      {/* Info panel */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
        <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-[#9CA3AF] mb-3">
          About the 24-Hour Forecast
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-[#6B7280] leading-relaxed">
          <div>
            <p className="mb-2">
              This map shows the <strong className="text-[#9CA3AF]">24-hour maximum</strong> lightning
              risk (LAI.v1) across the UK, covering 06Z on the issue date through 06Z the
              following day.
            </p>
            <p className="mb-2">
              The forecast is built by aggregating the four most recent LAI.v1 model runs
              (06Z, 00Z, 18Z, 12Z) and taking the per-grid-cell maximum over all forecast
              steps that fall within the 24-hour window.
            </p>
            <p>
              A Gaussian smoothing filter (σ = 1) is applied to produce natural, isolated
              storm pockets while preserving UK seasonal and spatial realism.
            </p>
          </div>
          <div>
            <p className="mb-2">
              <strong className="text-[#9CA3AF]">Convective filtering</strong> is applied —
              only storm-capable grid cells are shown:
            </p>
            <ul className="space-y-1 mb-2">
              <li>· CAPE ≥ 150 J/kg</li>
              <li>· Precipitation rate ≥ 0.1 mm/hr</li>
              <li>· Cloud-top temperature ≤ −20 °C</li>
            </ul>
            <p>
              The LAI.v1 scale runs from 0–10: values of 1–3 indicate weak activity, 4–6
              moderate, 7–9 strong/severe, and 10 extreme or violent convection. The
              forecast updates automatically every day at <strong className="text-[#9CA3AF]">09:00 UTC</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live Lightning — constants
// ---------------------------------------------------------------------------

const LIGHTNING_TIME_BUCKETS = [
  { maxMs: 2 * 60000,  color: "#FFFFFF", label: "< 2 min",  radius: 3 },
  { maxMs: 10 * 60000, color: "#FFFF00", label: "< 10 min", radius: 2.5 },
  { maxMs: 30 * 60000, color: "#FFA500", label: "< 30 min", radius: 2 },
  { maxMs: 60 * 60000, color: "#FF4444", label: "< 60 min", radius: 1.5 },
];

const LIGHTNING_TIME_WINDOWS = [
  { label: "5m",  seconds: 5 * 60 },
  { label: "15m", seconds: 15 * 60 },
  { label: "30m", seconds: 30 * 60 },
  { label: "1h",  seconds: 60 * 60 },
  { label: "24h", seconds: 24 * 60 * 60 },
];

const UK_LIGHTNING_BOUNDS     = { latMin: 49.5,      latMax: 61.5,      lonMin: -11,        lonMax: 3        };
const EUROPE_LIGHTNING_BOUNDS = { latMin: 29.382175, latMax: 70.318738, lonMin: -24.609375, lonMax: 45.878906 };

// Leaflet fitBounds arrays (SW, NE)
const EUROPE_BOUNDS = [[29.382175, -24.609375], [70.318738, 45.878906]];

// Maximum number of strikes rendered at once (performance protection)
const MAX_STRIKES = 5000;

// Bounds for the LAI 24h PNG image overlay – must match OVERLAY_EXTENT in
// generate_lai_24h_forecast.py: Lat 49–61, Lon −8–2.
const LAI_24H_OVERLAY_BOUNDS = [[49, -8], [61, 2]];

// ---------------------------------------------------------------------------
// Live Lightning — helpers
// ---------------------------------------------------------------------------

function getTodayLightningDate() {
  const now = new Date();
  if (now.getUTCHours() < 6) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  }
  return now.toISOString().slice(0, 10);
}

function computeLightningStats(strikes) {
  if (!strikes.length) return { total: 0, peakPerMin: 0 };

  const now = new Date();
  const dayStartMs = Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 6, 0, 0
  );

  // If it's before 06:00 UTC today, use yesterday's 06:00 UTC as start
  const effectiveStart =
    Date.now() >= dayStartMs ? dayStartMs : dayStartMs - 86400000;

  const todayStrikes = strikes.filter((s) => s.t * 1000 >= effectiveStart);

  // Peak strikes in any 60-second window
  let peakPerMin = 0;
  if (todayStrikes.length > 1) {
    for (let i = 0; i < todayStrikes.length; i++) {
      const t1 = todayStrikes[i].t;
      let count = 0;
      for (let j = i; j < todayStrikes.length; j++) {
        if (todayStrikes[j].t < t1 + 60) count++;
        else break;
      }
      if (count > peakPerMin) peakPerMin = count;
    }
  }

  return { total: todayStrikes.length, peakPerMin };
}

// ---------------------------------------------------------------------------
// Live Lightning — Canvas layer (draws directly on a canvas over the map)
// ---------------------------------------------------------------------------

function LightningCanvasLayer({ strikes, timeWindowMs, showStrikes, showDensity, replayTime, lightningBounds }) {
  const map = useMap();

  useEffect(() => {
    const mapContainer = map.getContainer();
    const canvas = document.createElement("canvas");
    canvas.className = "lightning-canvas-layer";
    canvas.style.cssText =
      "position:absolute;top:0;left:0;pointer-events:none;z-index:450;";
    mapContainer.appendChild(canvas);

    function redraw() {
      const size = map.getSize();
      if (canvas.width !== size.x) canvas.width = size.x;
      if (canvas.height !== size.y) canvas.height = size.y;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, size.x, size.y);

      if (!strikes.length) return;

      const nowMs = replayTime != null ? replayTime * 1000 : Date.now();

      // --- Density grid ---
      if (showDensity) {
        const GRID_W = 60;
        const GRID_H = 60;
        const grid = new Int32Array(GRID_W * GRID_H);
        const { latMin, latMax, lonMin, lonMax } = lightningBounds;
        const latRange = latMax - latMin;
        const lonRange = lonMax - lonMin;

        strikes.forEach((s) => {
          const ageMs = nowMs - s.t * 1000;
          if (ageMs < 0 || ageMs > timeWindowMs) return;
          const col = Math.min(
            GRID_W - 1,
            Math.max(0, Math.floor(((s.lon - lonMin) / lonRange) * GRID_W))
          );
          const row = Math.min(
            GRID_H - 1,
            Math.max(0, Math.floor(((s.lat - latMin) / latRange) * GRID_H))
          );
          grid[row * GRID_W + col]++;
        });

        let maxCount = 1;
        for (let i = 0; i < grid.length; i++) {
          if (grid[i] > maxCount) maxCount = grid[i];
        }

        for (let row = 0; row < GRID_H; row++) {
          for (let col = 0; col < GRID_W; col++) {
            const count = grid[row * GRID_W + col];
            if (count === 0) continue;
            const lat1 = latMin + (row / GRID_H) * latRange;
            const lat2 = latMin + ((row + 1) / GRID_H) * latRange;
            const lon1 = lonMin + (col / GRID_W) * lonRange;
            const lon2 = lonMin + ((col + 1) / GRID_W) * lonRange;
            const p1 = map.latLngToContainerPoint([lat2, lon1]);
            const p2 = map.latLngToContainerPoint([lat1, lon2]);
            const intensity = (count / maxCount) * 0.55;
            ctx.fillStyle = `rgba(255,200,50,${intensity.toFixed(3)})`;
            ctx.fillRect(
              p1.x, p1.y,
              Math.max(1, p2.x - p1.x),
              Math.max(1, p2.y - p1.y)
            );
          }
        }
      }

      // --- Individual strikes ---
      if (showStrikes) {
        let plotted = 0;
        strikes.forEach((s) => {
          const ageMs = nowMs - s.t * 1000;
          if (ageMs < 0 || ageMs > timeWindowMs) return;
          const point = map.latLngToContainerPoint([s.lat, s.lon]);
          plotted++;

          let color = "#FF4444";
          let radius = 1.5;
          for (const bucket of LIGHTNING_TIME_BUCKETS) {
            if (ageMs < bucket.maxMs) {
              color = bucket.color;
              radius = bucket.radius;
              break;
            }
          }

          const opacity = Math.max(0.15, 1 - (ageMs / timeWindowMs) * 0.75);
          ctx.globalAlpha = opacity;
          ctx.beginPath();
          ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
        });
        ctx.globalAlpha = 1;
        if (strikes.length > 0) {
          console.debug(
            `[Lightning] Canvas: ${plotted} / ${strikes.length} strike(s) plotted within time window`
          );
          if (plotted === 0) {
            console.warn(
              "[Lightning] ⚠️ Strikes exist but none plotted — all older than selected time window"
            );
          }
        }
      }
    }

    map.on("move zoom viewreset resize", redraw);
    redraw();

    return () => {
      map.off("move zoom viewreset resize", redraw);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [map, strikes, timeWindowMs, showStrikes, showDensity, replayTime, lightningBounds]);

  return null;
}

// ---------------------------------------------------------------------------
// Live Lightning tab component
// ---------------------------------------------------------------------------

function LiveLightningTab() {
  const [timeWindowIdx, setTimeWindowIdx] = useState(4); // default 24h
  const [showStrikes,   setShowStrikes]   = useState(true);
  const [showDensity,   setShowDensity]   = useState(true);
  const [overlayLAI,    setOverlayLAI]    = useState(false);
  const [mapMode,       setMapMode]       = useState("UK"); // "UK" | "Europe"

  // Live data
  const [strikes,     setStrikes]     = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dataStatus,  setDataStatus]  = useState("loading"); // "loading" | "ok" | "empty" | "error"
  const [stats,       setStats]       = useState({ total: 0, peakPerMin: 0 });

  // Archive
  const [archiveDate,    setArchiveDate]    = useState("");
  const [archiveStrikes, setArchiveStrikes] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError,   setArchiveError]   = useState(null);

  // Replay
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayTime,  setReplayTime]  = useState(null); // Unix timestamp (seconds)
  const [replaySpeed, setReplaySpeed] = useState(1);
  const replayIntervalRef = useRef(null);

  const timeWindowMs = LIGHTNING_TIME_WINDOWS[timeWindowIdx].seconds * 1000;

  // Return the correct data path for a given date and map mode.
  function getLightningDataPath(date, mode) {
    return mode === "Europe"
      ? `/data/lightning/europe/${date}.json`
      : `/data/lightning/${date}.json`;
  }

  // ---- Load today's data and refresh every 30 s ----
  useEffect(() => {
    let cancelled = false;

    function load() {
      const date = getTodayLightningDate();
      const path = getLightningDataPath(date, mapMode);
      console.debug(`[Lightning] Fetching ${path} (mode=${mapMode}, date=${date})`);
      fetch(path)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => {
          if (!cancelled) {
            // Support both the legacy plain-array format and the current
            // object format { period_start, period_end, points: [] }.
            const arr = Array.isArray(data)
              ? data
              : Array.isArray(data?.points)
              ? data.points
              : [];
            console.debug("[Lightning] Raw API response:", data);
            console.debug(`[Lightning] Total strikes in file: ${arr.length}`);

            if (arr.length > 0) {
              const minTs = Math.min(...arr.map((s) => s.t));
              const maxTs = Math.max(...arr.map((s) => s.t));
              console.debug(
                `[Lightning] Time range in file: ${new Date(minTs * 1000).toUTCString()} → ${new Date(maxTs * 1000).toUTCString()}`
              );

              // Validate client-side bounds filter to catch false-zero scenarios.
              const bounds = mapMode === "Europe" ? EUROPE_LIGHTNING_BOUNDS : UK_LIGHTNING_BOUNDS;
              const inBounds = arr.filter(
                (s) =>
                  s.lat >= bounds.latMin &&
                  s.lat <= bounds.latMax &&
                  s.lon >= bounds.lonMin &&
                  s.lon <= bounds.lonMax
              );
              console.debug(
                `[Lightning] After ${mapMode} bounds filter: ${inBounds.length} / ${arr.length}`
              );
              if (arr.length > 0 && inBounds.length === 0) {
                console.warn(
                  `[Lightning] ⚠️ Bounds filter removed ALL ${arr.length} strike(s) — ` +
                  `check bounds: lat [${bounds.latMin}, ${bounds.latMax}], ` +
                  `lon [${bounds.lonMin}, ${bounds.lonMax}]`
                );
              }
            } else {
              console.debug("[Lightning] File contains 0 strikes (connection working, no activity).");
            }

            setStrikes(arr);
            // Use the pipeline's own fetched_at timestamp so "Last updated"
            // reflects when the ingestion run completed, not when the browser
            // polled the file.  Fall back to the current time only if the
            // field is absent (e.g. legacy files written before this field
            // was added).
            const pipelineTime = data?.fetched_at
              ? new Date(data.fetched_at).toUTCString()
              : new Date().toUTCString();
            setLastUpdated(pipelineTime);
            setDataStatus(arr.length === 0 ? "empty" : "ok");
            setStats(computeLightningStats(arr));
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error(`[Lightning] ❌ Failed to fetch lightning data: ${err.message}`);
            setStrikes([]);
            setDataStatus("error");
          }
        });
    }

    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [mapMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear archive data when map mode changes so stale regional data is not shown.
  useEffect(() => {
    setArchiveStrikes(null);
    setArchiveDate("");
    setArchiveError(null);
    stopReplay();
  }, [mapMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Archive loader ----
  function loadArchive(date) {
    if (!date) return;
    setArchiveLoading(true);
    setArchiveError(null);
    setArchiveStrikes(null);
    stopReplay();
    fetch(getLightningDataPath(date, mapMode))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setArchiveStrikes(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.points)
            ? data.points
            : []
        );
        setArchiveLoading(false);
      })
      .catch(() => {
        setArchiveError("No data available for this date.");
        setArchiveLoading(false);
      });
  }

  // ---- Replay controls ----
  function stopReplay() {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
    setIsReplaying(false);
    setReplayTime(null);
  }

  function startReplay() {
    const src = archiveStrikes ?? strikes;
    if (!src || src.length === 0) return;
    stopReplay();
    const sorted = [...src].sort((a, b) => a.t - b.t);
    let simTime = sorted[0].t;
    const endTime = sorted[sorted.length - 1].t;
    // advance by (60 * replaySpeed) simulated seconds per real second
    const stepSecs = 60 * replaySpeed;

    setIsReplaying(true);
    setReplayTime(simTime);
    replayIntervalRef.current = setInterval(() => {
      simTime += stepSecs;
      if (simTime >= endTime) {
        setReplayTime(endTime);
        stopReplay();
        return;
      }
      setReplayTime(simTime);
    }, 1000);
  }

  // Cleanup on unmount
  useEffect(() => () => stopReplay(), []);

  // ---- Derive display strikes ----
  const liveOrArchiveStrikes = archiveStrikes != null ? archiveStrikes : strikes;

  // Client-side bounds filter based on selected map mode
  const activeLightningBounds = mapMode === "UK" ? UK_LIGHTNING_BOUNDS : EUROPE_LIGHTNING_BOUNDS;
  const boundsFiltered = liveOrArchiveStrikes.filter(
    (s) =>
      s.lat >= activeLightningBounds.latMin &&
      s.lat <= activeLightningBounds.latMax &&
      s.lon >= activeLightningBounds.lonMin &&
      s.lon <= activeLightningBounds.lonMax
  );
  if (liveOrArchiveStrikes.length > 0 && boundsFiltered.length === 0) {
    console.warn(
      `[Lightning] ⚠️ Client bounds filter removed all ${liveOrArchiveStrikes.length} strike(s) ` +
      `for mode="${mapMode}". Bounds: lat [${activeLightningBounds.latMin}, ${activeLightningBounds.latMax}], ` +
      `lon [${activeLightningBounds.lonMin}, ${activeLightningBounds.lonMax}]`
    );
  }

  // Performance cap: keep the most-recent MAX_STRIKES strikes
  const cappedStrikes =
    boundsFiltered.length > MAX_STRIKES
      ? boundsFiltered.slice(boundsFiltered.length - MAX_STRIKES)
      : boundsFiltered;

  // During replay, show strikes up to replayTime with the time-window look-back
  const displayStrikes =
    replayTime != null
      ? cappedStrikes.filter(
          (s) => s.t <= replayTime && s.t * 1000 >= replayTime * 1000 - timeWindowMs
        )
      : cappedStrikes;

  const displayReplayTime = replayTime;

  // ---- Replay-time label ----
  const replayLabel = replayTime != null
    ? new Date(replayTime * 1000).toUTCString()
    : null;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="bg-[#111827] border border-white/10 rounded-lg px-5 py-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-[#FBBF24] mb-1">
              LIVE LIGHTNING — UK Lightning Activity
            </h2>
            <p className="text-xs text-[#9CA3AF]">Data source: Blitzortung Network</p>
            <p className="text-xs text-[#6B7280]">
              Daily lightning period: 06:00 UTC → 06:00 UTC next day
            </p>
            {lastUpdated && (
              <p className="text-xs text-[#6B7280] mt-0.5">Last updated: {lastUpdated}</p>
            )}
          </div>
          {/* System status badge */}
          <div className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
            style={{
              background: dataStatus === "ok"
                ? "rgba(16,185,129,0.15)"
                : dataStatus === "empty"
                ? "rgba(251,191,36,0.15)"
                : dataStatus === "error"
                ? "rgba(239,68,68,0.15)"
                : "rgba(107,114,128,0.15)",
            }}
          >
            <span>
              {dataStatus === "ok" ? "🟢" : dataStatus === "empty" ? "🟡" : dataStatus === "error" ? "🔴" : "⏳"}
            </span>
            <span className={
              dataStatus === "ok" ? "text-emerald-400" :
              dataStatus === "empty" ? "text-[#FBBF24]" :
              dataStatus === "error" ? "text-red-400" :
              "text-[#9CA3AF]"
            }>
              {dataStatus === "ok" ? "Live Data Active" :
               dataStatus === "empty" ? "No Lightning Detected" :
               dataStatus === "error" ? "Data Unavailable" :
               "Loading…"}
            </span>
          </div>
        </div>
      </div>

      {/* Main layout: map + right panel */}
      <div className="lg:flex lg:gap-4 lg:items-start">

        {/* ---- Map ---- */}
        <div className="lg:flex-1">
          <div
            className="rounded-lg overflow-hidden border border-white/10 relative w-full md:w-1/2 lg:w-full mx-auto"
            style={{ aspectRatio: "1 / 1", minHeight: "200px" }}
          >
            {/* Empty / error overlay — semi-transparent so map stays visible */}
            {dataStatus !== "ok" && !archiveStrikes && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#0B1220]/75 z-[999]">
                <div className="text-center space-y-2 p-6 bg-black/60 rounded-xl border border-white/10">
                  {dataStatus === "loading" && (
                    <>
                      <div className="w-8 h-8 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-sm text-[#9CA3AF]">Loading lightning data…</p>
                    </>
                  )}
                  {dataStatus === "error" && (
                    <>
                      <p className="text-2xl leading-none">🔴</p>
                      <p className="text-sm text-[#9CA3AF]">Data Unavailable / API Error</p>
                      <p className="text-xs text-[#6B7280]">
                        Data ingestion begins once the GitHub Actions pipeline runs.
                      </p>
                      <p className="text-xs text-[#6B7280]">System active and monitoring</p>
                    </>
                  )}
                  {dataStatus === "empty" && (
                    <>
                      <p className="text-2xl leading-none">⚡</p>
                      <p className="text-sm text-[#9CA3AF]">No lightning detected in selected time window</p>
                      <p className="text-xs text-[#6B7280]">System active and monitoring</p>
                    </>
                  )}
                </div>
              </div>
            )}

            <MapContainer
              center={MAP_CENTER}
              zoom={MAP_ZOOM}
              style={{ height: "100%", width: "100%" }}
              zoomControl
              aria-label="Lightning strike visualization map for the United Kingdom"
            >
              <MapResizer bounds={mapMode === "UK" ? UK_BOUNDS : EUROPE_BOUNDS} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <LightningCanvasLayer
                strikes={displayStrikes}
                timeWindowMs={timeWindowMs}
                showStrikes={showStrikes}
                showDensity={showDensity}
                replayTime={displayReplayTime}
                lightningBounds={activeLightningBounds}
              />
              {overlayLAI && (
                <ImageOverlay
                  url={OVERLAY_URL}
                  bounds={LAI_24H_OVERLAY_BOUNDS}
                  opacity={0.45}
                />
              )}
            </MapContainer>

            {/* Strike-age legend */}
            <div
              className="absolute bottom-2 left-2 z-[1000] bg-black/70 rounded px-2 py-1.5 space-y-0.5"
              style={{ pointerEvents: "none" }}
            >
              {LIGHTNING_TIME_BUCKETS.map((b) => (
                <div key={b.maxMs} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: b.color }}
                  />
                  <span className="text-[10px] text-white/80">{b.label}</span>
                </div>
              ))}
            </div>

            {/* Replay time overlay */}
            {replayLabel && (
              <div
                className="absolute top-2 right-2 z-[1000] bg-black/70 rounded px-2 py-1 text-[11px] text-[#FBBF24] font-mono"
                style={{ pointerEvents: "none" }}
              >
                {replayLabel}
              </div>
            )}
          </div>
        </div>

        {/* ---- Right panel ---- */}
        <div className="mt-4 lg:mt-0 lg:w-72 xl:w-80 flex flex-col gap-3">

          {/* Map view toggle */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Map View
            </h3>
            <div className="flex gap-1">
              {[{ label: "UK", value: "UK" }, { label: "Europe", value: "Europe" }].map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setMapMode(value)}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors ${
                    mapMode === value
                      ? "bg-[#FBBF24] text-[#1a1a1a]"
                      : "bg-white/5 text-[#9CA3AF] hover:bg-white/10 hover:text-[#F9FAFB]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {mapMode === "Europe" && (
              <p className="text-[10px] text-[#9CA3AF] mt-2">
                Higher activity region — performance optimised
              </p>
            )}
          </div>

          {/* Time window */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Lightning Time Window
            </h3>
            <div className="flex gap-1 flex-wrap">
              {LIGHTNING_TIME_WINDOWS.map((w, i) => (
                <button
                  key={w.label}
                  onClick={() => setTimeWindowIdx(i)}
                  className={`px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors ${
                    timeWindowIdx === i
                      ? "bg-[#FBBF24] text-[#1a1a1a]"
                      : "bg-white/5 text-[#9CA3AF] hover:bg-white/10 hover:text-[#F9FAFB]"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Toggle options */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Display Options
            </h3>
            <div className="space-y-2">
              {[
                { label: "Show lightning strikes",         id: "toggle-strikes",  value: showStrikes, setter: setShowStrikes },
                { label: "Show lightning density",         id: "toggle-density",  value: showDensity, setter: setShowDensity },
                { label: "Overlay LAI 24-hour forecast",   id: "toggle-lai",      value: overlayLAI,  setter: setOverlayLAI  },
              ].map(({ label, id, value, setter }) => (
                <label key={id} htmlFor={id} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    id={id}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setter(e.target.checked)}
                    className="w-3.5 h-3.5 accent-yellow-400 cursor-pointer"
                  />
                  <span className="text-xs text-[#9CA3AF] group-hover:text-[#F9FAFB] transition-colors">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Daily statistics */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Daily Lightning Statistics (UK)
            </h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">Strikes since 06:00 UTC</span>
                <span className="font-mono font-semibold text-[#FBBF24]">
                  {stats.total.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">Peak strikes / minute</span>
                <span className="font-mono font-semibold text-[#FBBF24]">
                  {stats.peakPerMin}
                </span>
              </div>
            </div>
          </div>

          {/* Archive viewer */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Lightning Archive
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="date"
                  value={archiveDate}
                  onChange={(e) => setArchiveDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  aria-label="Select archive date"
                  className="flex-1 bg-[#1a1a1a] border border-white/10 text-[#F9FAFB] text-xs rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FBBF24]"
                />
                <button
                  onClick={() => loadArchive(archiveDate)}
                  disabled={!archiveDate}
                  className="px-3 py-1.5 bg-[#FBBF24] text-[#1a1a1a] text-xs font-semibold rounded uppercase tracking-wide disabled:opacity-40 hover:bg-[#F59E0B] transition-colors"
                >
                  Load
                </button>
              </div>

              {archiveLoading && (
                <p className="text-xs text-[#9CA3AF]">Loading archive…</p>
              )}
              {archiveError && (
                <p className="text-xs text-red-400">{archiveError}</p>
              )}

              {archiveStrikes != null && !archiveLoading && (
                <div className="space-y-2">
                  <p className="text-xs text-[#9CA3AF]">
                    {archiveStrikes.length.toLocaleString()} strikes loaded
                  </p>

                  {/* Replay speed selector */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-[#6B7280] shrink-0">Speed:</span>
                    {[1, 5, 10].map((s) => (
                      <button
                        key={s}
                        onClick={() => { setReplaySpeed(s); stopReplay(); }}
                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                          replaySpeed === s
                            ? "bg-[#FBBF24] text-[#1a1a1a]"
                            : "bg-white/5 text-[#9CA3AF] hover:bg-white/10"
                        }`}
                      >
                        {s}×
                      </button>
                    ))}
                  </div>

                  {/* Play / Stop / Clear */}
                  <div className="flex gap-2">
                    {!isReplaying ? (
                      <button
                        onClick={startReplay}
                        className="flex-1 py-1.5 bg-[#FBBF24] text-[#1a1a1a] text-xs font-semibold rounded uppercase tracking-wide hover:bg-[#F59E0B] transition-colors"
                      >
                        ▶ Replay
                      </button>
                    ) : (
                      <button
                        onClick={stopReplay}
                        className="flex-1 py-1.5 bg-red-500 text-white text-xs font-semibold rounded uppercase tracking-wide hover:bg-red-600 transition-colors"
                      >
                        ■ Stop
                      </button>
                    )}
                    <button
                      onClick={() => { setArchiveStrikes(null); setArchiveDate(""); stopReplay(); }}
                      className="px-3 py-1.5 bg-white/5 text-[#9CA3AF] text-xs font-semibold rounded uppercase tracking-wide hover:bg-white/10 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LAI forecast verification */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-4">
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#9CA3AF] mb-3">
              Lightning vs LAI Forecast
            </h3>
            <p className="text-xs text-[#6B7280] leading-relaxed">
              Enable the <strong className="text-[#9CA3AF]">Overlay LAI 24-hour forecast</strong> toggle
              and allow strike data to accumulate. The verification panel shows the distribution
              of observed strikes across LAI forecast categories once both datasets are available.
            </p>
            {overlayLAI && strikes.length > 0 && (
              <LaiVerificationPanel strikes={strikes} timeWindowMs={timeWindowMs} />
            )}
          </div>
        </div>
      </div>

      {/* Info panel */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5 mb-16">
        <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-[#9CA3AF] mb-3">
          About Live Lightning
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-[#6B7280] leading-relaxed">
          <div>
            <p className="mb-2">
              Lightning strike data is sourced from the{" "}
              <strong className="text-[#9CA3AF]">Blitzortung Network</strong>, a global
              crowdsourced lightning detection system with hundreds of receiving stations
              across Europe and the UK. Strikes are ingested every few minutes via an
              automated GitHub Actions pipeline.
            </p>
            <p className="mb-2">
              UK strikes (49.5°N–61.5°N, 11°W–3°E) are stored in the UK dataset.
              Europe-wide strikes (29.4°N–70.3°N, 24.6°W–45.9°E) are stored
              separately and displayed when the Europe map view is selected.
            </p>
            <p>
              The daily period runs from{" "}
              <strong className="text-[#9CA3AF]">06:00 UTC</strong> to{" "}
              <strong className="text-[#9CA3AF]">06:00 UTC</strong> the following day,
              matching the LAI.v1 forecast window.
            </p>
          </div>
          <div>
            <p className="mb-2">Strike age colour coding:</p>
            <ul className="space-y-1 mb-2">
              <li>· <span className="text-white">White</span> — less than 2 minutes old</li>
              <li>· <span className="text-yellow-400">Yellow</span> — less than 10 minutes old</li>
              <li>· <span className="text-orange-400">Orange</span> — less than 30 minutes old</li>
              <li>· <span className="text-red-400">Red</span> — less than 60 minutes old</li>
            </ul>
            <p>
              Older strikes fade out gradually. Use the time-window control to limit the
              look-back period. The archive viewer allows you to select any recorded date and
              replay lightning activity chronologically at 1×, 5×, or 10× speed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LAI Verification panel (shown when overlay is active and data available)
// ---------------------------------------------------------------------------

function LaiVerificationPanel({ strikes, timeWindowMs }) {
  const nowMs = Date.now();
  const relevant = strikes.filter((s) => nowMs - s.t * 1000 <= timeWindowMs);

  if (relevant.length === 0) {
    return (
      <p className="text-xs text-[#6B7280] mt-2">
        No strikes in the current time window.
      </p>
    );
  }

  // Approximate LAI category from latitude (simplified proxy):
  //   higher latitude → generally weaker convection in UK
  //   This is a rough approximation; full verification requires a spatial join.
  const cats = { "1–3": 0, "4–6": 0, "7–9": 0, "10": 0 };
  relevant.forEach((s) => {
    if (s.lat > 56) cats["1–3"]++;
    else if (s.lat > 53) cats["4–6"]++;
    else if (s.lat > 51) cats["7–9"]++;
    else cats["10"]++;
  });
  const total = relevant.length;
  const catColors = { "1–3": "#A8CFB6", "4–6": "#F2BF42", "7–9": "#F33D14", "10": "#B45AA9" };

  return (
    <div className="mt-3 space-y-2 text-xs">
      {Object.entries(cats).map(([cat, count]) => {
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={cat} className="flex items-center gap-2">
            <span className="text-[#6B7280] w-10 shrink-0">LAI {cat}</span>
            <div className="flex-1 h-1.5 bg-white/10 rounded overflow-hidden">
              <div
                className="h-full rounded"
                style={{ width: `${pct}%`, backgroundColor: catColors[cat] }}
              />
            </div>
            <span className="font-mono text-[#9CA3AF] w-8 text-right">
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
      <p className="text-[10px] text-[#6B7280] mt-1">
        Based on {total.toLocaleString()} strikes · approximate lat-based proxy
      </p>
    </div>
  );
}

// Inner component: fits the map to the supplied bounds and invalidates size on mount
function MapResizer({ bounds }) {
  const map = useMap();
  const initialised = useRef(false);
  useEffect(() => {
    if (!initialised.current) {
      map.invalidateSize();
      initialised.current = true;
    }
    map.fitBounds(bounds, { animate: true, padding: [10, 10] });
  }, [map, bounds]);
  return null;
}

// ---------------------------------------------------------------------------
// Forecast Animation (GIF) tab component
// ---------------------------------------------------------------------------

/**
 * Format a run ID into a human-readable dropdown label showing the run hour
 * and a relative day indicator (Today / Yesterday / DD/MM/YY).
 */
function formatAnimRunLabel(runId) {
  if (!runId) return "";
  const match = runId.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})z$/);
  if (!match) return runId;
  const [, yyyy, mm, dd, hh] = match;
  const runDate = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  const now = new Date();
  const todayUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  const diffDays = Math.round((todayUTC - runDate) / 86400000);
  let dayLabel;
  if (diffDays === 0) dayLabel = "Today";
  else if (diffDays === 1) dayLabel = "Yesterday";
  else dayLabel = `${dd}/${mm}/${String(yyyy).slice(2)}`;
  return `${hh}Z (${dayLabel})`;
}

function LAIForecastAnimationTab() {
  const [gifStatus, setGifStatus] = useState("loading"); // "loading" | "ok" | "error"
  const [animRunIndex, setAnimRunIndex] = useState(null); // { latest, runs }
  const [animSelectedRun, setAnimSelectedRun] = useState(null); // "YYYYMMDD_HHz"

  // Fetch run index on mount to populate the run selector
  useEffect(() => {
    fetch(INDEX_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((idx) => {
        setAnimRunIndex(idx);
        setAnimSelectedRun(idx.latest ?? null);
      })
      .catch(() => {
        // Index unavailable — the GIF img will trigger its own error state
        setAnimRunIndex(null);
        setAnimSelectedRun(null);
      });
  }, []);

  // Derive the GIF URL from the selected run
  const gifUrl = animSelectedRun
    ? `/data/lai/lai_animation_${animSelectedRun}.gif`
    : null;

  // Reset load status whenever the selected run (and therefore URL) changes
  useEffect(() => {
    if (gifUrl) {
      setGifStatus("loading");
    }
  }, [gifUrl]);

  // Four most recent runs for selection (newest first)
  const recentRuns = animRunIndex?.runs
    ? [...animRunIndex.runs].reverse().slice(0, 4)
    : [];

  // Shared download button — rendered in both mobile and desktop panels
  const downloadButton = gifStatus === "ok" && gifUrl ? (
    <a
      href={gifUrl}
      download={`lai_animation_${animSelectedRun}.gif`}
      className="inline-flex items-center gap-2 px-4 py-2 rounded text-xs font-semibold uppercase tracking-widest text-[#1a1a1a] bg-[#FBBF24] hover:bg-[#F59E0B] transition-colors"
    >
      {/* Download icon */}
      <svg
        className="w-3.5 h-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      Download GIF
    </a>
  ) : null;

  return (
    <div className="space-y-4">
      {/* Mobile-only: run info + run buttons shown stacked above the GIF.
          On desktop (lg+), these controls move to the right-hand panel
          alongside the GIF instead — see the two-column layout below. */}
      <div className="lg:hidden">
        {animRunIndex && (
          <div className="bg-[#111827] border border-white/10 rounded-lg px-4 py-3 flex flex-col gap-3">
            <span className="text-xs text-[#9CA3AF]">
              {animSelectedRun && animSelectedRun === animRunIndex.latest
                ? "Showing latest run: "
                : "Showing run: "}
              <span className="font-mono font-semibold text-[#FBBF24]">
                {animSelectedRun ? formatAnimRunLabel(animSelectedRun) : "—"}
              </span>
            </span>
            {/* Last 4 runs shown as clickable buttons — no dropdown needed */}
            {recentRuns.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {recentRuns.map((run) => (
                  <button
                    key={run}
                    onClick={() => setAnimSelectedRun(run)}
                    className={`px-3 py-1.5 rounded text-xs font-mono transition-colors border ${
                      run === animSelectedRun
                        ? "bg-[#FBBF24]/20 text-[#FBBF24] font-bold border-[#FBBF24]/30"
                        : "text-[#6B7280] hover:text-[#9CA3AF] border-white/10 hover:bg-white/5"
                    }`}
                  >
                    {formatAnimRunLabel(run)}
                    {run === animRunIndex.latest ? " · latest" : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {gifStatus === "ok" && animSelectedRun && (
          <p className="text-xs text-[#6B7280]">
            {formatAnimRunLabel(animSelectedRun)} · T+0 to T+48 in 3-hourly steps
          </p>
        )}
      </div>

      {/* Desktop layout: GIF on the left, run controls on the right.
          Mobile: GIF is stacked full-width below the controls above.
          The side-by-side layout only activates at the lg breakpoint (~1024 px). */}
      <div className="lg:flex lg:gap-6 lg:items-start">

        {/* GIF display — full width on mobile, flexible left column on desktop */}
        <div className="lg:flex-1">
          <div
            className="rounded-lg overflow-hidden border border-white/10 bg-[#111827] flex items-center justify-center mx-auto w-full"
            style={{ minHeight: "200px" }}
          >
            {gifStatus === "loading" && (
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="w-8 h-8 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-[#9CA3AF]">Loading forecast animation…</p>
              </div>
            )}
            {gifStatus === "error" && (
              <div className="text-center space-y-2 p-8">
                <p className="text-sm text-red-400">Forecast animation not yet available.</p>
                <p className="text-xs text-[#6B7280]">
                  Animations are generated after each LAI.v1 run (00Z, 06Z, 12Z, 18Z). Please check back later.
                </p>
              </div>
            )}
            {/* The animated GIF auto-plays in the browser once loaded */}
            {gifUrl && (
              <img
                key={gifUrl}
                src={gifUrl}
                alt="Animated LAI.v1 lightning risk forecast for the UK (T+0 to T+48)"
                className={`w-full h-auto rounded-lg ${gifStatus !== "ok" ? "hidden" : ""}`}
                onLoad={() => setGifStatus("ok")}
                onError={() => setGifStatus("error")}
              />
            )}
          </div>

          {/* Mobile-only download button — stacked below the GIF.
              On desktop, the download button appears in the right-hand panel. */}
          {gifStatus === "ok" && gifUrl && (
            <div className="flex justify-end mt-3 lg:hidden">
              {downloadButton}
            </div>
          )}
        </div>

        {/* Desktop-only right panel: latest run info + last 4 run time buttons + download.
            Hidden on mobile — those controls appear stacked above/below the GIF instead.
            Only shown at the lg breakpoint and above. */}
        <div className="hidden lg:flex lg:flex-col lg:w-72 xl:w-80 gap-3 lg:sticky lg:top-4">
          {animRunIndex && (
            <div className="bg-[#111827] border border-white/10 rounded-lg px-4 py-4 flex flex-col gap-4">
              {/* Latest run info */}
              <div>
                <h3 className="text-xs font-medium uppercase tracking-widest text-[#9CA3AF] mb-1.5">
                  Latest Run
                </h3>
                <span className="text-xs text-[#9CA3AF]">
                  {animSelectedRun && animSelectedRun === animRunIndex.latest
                    ? "Showing latest run: "
                    : "Showing run: "}
                  <span className="font-mono font-semibold text-[#FBBF24]">
                    {animSelectedRun ? formatAnimRunLabel(animSelectedRun) : "—"}
                  </span>
                </span>
                {gifStatus === "ok" && animSelectedRun && (
                  <p className="text-xs text-[#6B7280] mt-1">
                    T+0 to T+48 in 3-hourly steps
                  </p>
                )}
              </div>

              {/* Last 4 animated run times shown as direct clickable buttons —
                  no dropdown; runs are immediately visible and selectable */}
              {recentRuns.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-widest text-[#9CA3AF] mb-2">
                    Recent Runs
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    {recentRuns.map((run) => (
                      <button
                        key={run}
                        onClick={() => setAnimSelectedRun(run)}
                        className={`w-full text-left px-3 py-2 rounded text-xs font-mono transition-colors border ${
                          run === animSelectedRun
                            ? "bg-[#FBBF24]/20 text-[#FBBF24] font-bold border-[#FBBF24]/30"
                            : "text-[#6B7280] hover:text-[#9CA3AF] border-white/10 hover:bg-white/5"
                        }`}
                      >
                        {formatAnimRunLabel(run)}
                        {run === animRunIndex.latest && (
                          <span className="ml-2 text-[#FBBF24] text-[10px] font-sans font-normal uppercase tracking-wide">
                            latest
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Download GIF button — reuses the shared downloadButton element */}
              {downloadButton && (
                <div>{downloadButton}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info panel */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
        <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-[#9CA3AF] mb-3">
          About the Forecast Animation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-[#6B7280] leading-relaxed">
          <div>
            <p className="mb-2">
              Each animation corresponds to a{" "}
              <strong className="text-[#9CA3AF]">single LAI.v1 model run</strong> (00Z,
              06Z, 12Z, or 18Z). It shows the forecast progression from{" "}
              <strong className="text-[#9CA3AF]">T+0 to T+48</strong> using the data from
              that run alone — no blending across runs.
            </p>
            <p className="mb-2">
              Frames advance in{" "}
              <strong className="text-[#9CA3AF]">3-hour intervals</strong>, giving 17
              frames per animation (T+0, T+3, T+6, … T+48).
            </p>
            <p>
              Select from the four most recent runs using the buttons above. The latest
              run is selected by default when the page loads.
            </p>
          </div>
          <div>
            <p className="mb-2">
              <strong className="text-[#9CA3AF]">Frame annotations:</strong>
            </p>
            <ul className="space-y-1 mb-2">
              <li>· <strong className="text-[#9CA3AF]">FH: T+X</strong> — forecast lead time (upper left)</li>
              <li>· <strong className="text-[#9CA3AF]">Valid: HH:MM UTC</strong> — valid time (upper right)</li>
              <li>· <strong className="text-[#9CA3AF]">Run: HHz</strong> — model cycle (figure title)</li>
            </ul>
            <p>
              The animation is pre-generated by a Python pipeline using{" "}
              <span className="font-mono text-[#9CA3AF]">matplotlib</span>,{" "}
              <span className="font-mono text-[#9CA3AF]">cartopy</span>, and{" "}
              <span className="font-mono text-[#9CA3AF]">Pillow</span>, and updates
              automatically after each LAI.v1 model run at{" "}
              <strong className="text-[#9CA3AF]">00Z, 06Z, 12Z, and 18Z</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LAIv1Page() {
  const [activeTab, setActiveTab] = useState("forecast");   // "forecast" | "24h" | "animation" | "lightning"
  const [geojsonData, setGeojsonData]   = useState(null);
  const [selectedFH, setSelectedFH]     = useState(0);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState(null);   // run data load error
  const [generatedAt, setGeneratedAt]   = useState(null);

  // Run selector state
  const [runIndex, setRunIndex]         = useState(null);   // { latest, runs }
  const [selectedRun, setSelectedRun]   = useState(null);   // "YYYYMMDD_HHz"

  const geoJsonRef = useRef(null);

  // ---- Preserve valid forecast time when switching runs ----
  const handleRunChange = (newRun) => {
    const currentRunMs = getRunTimeMs(selectedRun);
    const newRunMs     = getRunTimeMs(newRun);
    if (currentRunMs !== null && newRunMs !== null) {
      // Compute the valid time for the current run+forecastHour, then find the
      // corresponding forecast hour in the new run.
      const currentValidMs = currentRunMs + selectedFH * MS_PER_HOUR;
      const newFHHours = (currentValidMs - newRunMs) / MS_PER_HOUR;
      // Snap to the nearest available forecast step
      const snapped = FORECAST_HOURS.reduce((prev, curr) =>
        Math.abs(curr - newFHHours) < Math.abs(prev - newFHHours) ? curr : prev
      );
      // Clamp within [0, 36]
      const clamped = Math.max(FORECAST_HOURS[0], Math.min(FORECAST_HOURS[FORECAST_HOURS.length - 1], snapped));
      setSelectedFH(clamped);
    }
    setSelectedRun(newRun);
  };

  // ---- Load run index on mount ----
  useEffect(() => {
    fetch(INDEX_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((idx) => {
        setRunIndex(idx);
        setSelectedRun(idx.latest ?? null);
      })
      .catch(() => {
        // Index not yet available — fall back to null (no run selector shown)
        setRunIndex(null);
        setSelectedRun(null);
      });
  }, []);

  // ---- Load run data whenever selectedRun changes ----
  useEffect(() => {
    if (!selectedRun) return;

    setLoading(true);
    setLoadError(null);

    fetch(`${RUNS_URL}/${selectedRun}.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setGeojsonData(data);
        setGeneratedAt(data.generated_at ?? null);
        setLoading(false);
        // Debug: log LAI value distribution across all features
        const features = data.features ?? [];
        if (features.length > 0) {
          const laiValues = features
            .map((f) => {
              const p = f?.properties ?? {};
              return p.lai_value ?? p.lai ?? Number(p.intensity) ?? null;
            })
            .filter((v) => v != null && !isNaN(v));
          const minLAI = laiValues.length ? Math.min(...laiValues) : null;
          const maxLAI = laiValues.length ? Math.max(...laiValues) : null;
          const validCells = laiValues.filter((v) => v >= 1).length;
          console.debug(
            `[LAI] Run ${selectedRun}: ${features.length} features — min=${minLAI} max=${maxLAI} valid(≥1)=${validCells}`
          );
        } else {
          console.debug(`[LAI] Run ${selectedRun}: 0 features loaded`);
        }
      })
      .catch(() => {
        setGeojsonData(null);
        setLoadError("Run data unavailable");
        setLoading(false);
      });
  }, [selectedRun]);

  // ---- Filter features by the selected forecast hour ----
  const filteredData = geojsonData
    ? {
        ...geojsonData,
        features: (geojsonData.features ?? []).filter(
          (f) => f?.properties?.forecast_hour === selectedFH
        ),
      }
    : null;

  // ---- Force react-leaflet to re-render the GeoJSON layer when filter changes ----
  const geoJsonKey = `${selectedRun}-${selectedFH}-${filteredData?.features?.length ?? 0}`;

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-[#F9FAFB]">
      {/* ------------------------------------------------------------------ */}
      {/* Header + Tabs                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl pt-8 pb-0">
        <div className="flex flex-col gap-1 mb-4">
          <h1 className="font-heading text-2xl font-bold uppercase tracking-widest text-[#FBBF24]">
            LAI.v1
          </h1>
          <p className="text-sm text-[#9CA3AF]">
            Lightning Activity Index — Blended multi-model forecast (GFS 20% · ICON-EU 30% · ECMWF 30% · UKV 20%)
          </p>
          {generatedAt && activeTab === "forecast" && (
            <p className="text-xs text-[#6B7280]">
              Last updated: {generatedAt}
            </p>
          )}
        </div>

        {/* Tab navigation */}
        <div className="flex gap-0 border-b border-white/10 mb-4 overflow-x-auto">
          {[
            { id: "forecast",  label: "LAI Forecast" },
            { id: "24h",       label: "LAI 24 Hour Forecast" },
            { id: "animation", label: "Forecast Animation" },
            { id: "lightning", label: "Live Lightning" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`whitespace-nowrap px-5 py-3 text-xs uppercase tracking-widest font-medium border-b-2 transition-colors ${
                activeTab === id
                  ? "border-[#FBBF24] text-[#FBBF24]"
                  : "border-transparent text-[#9CA3AF] hover:text-[#F9FAFB]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Run selector bar + time slider — LAI Forecast tab only          */}
        {/* Mobile-only: stacked above the map.                             */}
        {/* On desktop (lg+), these controls move to the right-hand panel   */}
        {/* alongside the map — see the two-column layout in the content     */}
        {/* section below. They are hidden here at the lg breakpoint.        */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === "forecast" && (
          <div className="lg:hidden">
            <div className="bg-[#111827] border border-white/10 rounded-lg px-4 py-3 mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[#9CA3AF]">
                  {runIndex && selectedRun && selectedRun !== runIndex.latest
                    ? "Showing run: "
                    : "Showing latest run: "}
                  <span className="font-mono font-semibold text-[#FBBF24]">
                    {selectedRun ? formatRunLabel(selectedRun) : "—"}
                  </span>
                </span>
              </div>

              {runIndex && runIndex.runs && runIndex.runs.length > 1 && (
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="run-select"
                    className="text-xs text-[#6B7280] whitespace-nowrap"
                  >
                    View previous runs
                  </label>
                  <select
                    id="run-select"
                    value={selectedRun ?? ""}
                    onChange={(e) => handleRunChange(e.target.value)}
                    className="bg-[#1a1a1a] border border-white/10 text-[#F9FAFB] text-xs rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#FBBF24]"
                  >
                    {[...runIndex.runs].reverse().map((run) => (
                      <option key={run} value={run}>
                        {formatRunLabel(run)}
                        {run === runIndex.latest ? " (latest)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* ------------------------------------------------------------ */}
            {/* Time Slider                                                   */}
            {/* ------------------------------------------------------------ */}
            <div className="bg-[#111827] border border-white/10 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-widest text-[#9CA3AF]">
                  Forecast Hour
                </span>
                <span className="font-mono text-sm font-semibold text-[#FBBF24]">
                  T+{selectedFH}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={FORECAST_HOURS.length - 1}
                step={1}
                value={FORECAST_HOURS.indexOf(selectedFH)}
                onChange={(e) => setSelectedFH(FORECAST_HOURS[Number(e.target.value)])}
                className="w-full accent-yellow-400 cursor-pointer"
                aria-label="Forecast hour selector"
              />
              <div className="flex justify-between mt-1">
                {FORECAST_HOURS.map((fh) => (
                  <button
                    key={fh}
                    onClick={() => setSelectedFH(fh)}
                    className={`text-[10px] font-mono px-1 rounded transition-colors ${
                      fh === selectedFH
                        ? "text-[#FBBF24] font-bold"
                        : "text-[#6B7280] hover:text-[#9CA3AF]"
                    }`}
                  >
                    T+{fh}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* LAI Forecast tab content                                            */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === "forecast" && (
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Desktop layout: map on the left, run controls on the right.
              Mobile: stacked layout — map full width, controls are in the
              header section above (hidden at lg+, see lg:hidden wrapper). */}
          <div className="lg:flex lg:gap-6 lg:items-start">

            {/* Left column: map + legend — full width on mobile, flex-1 on desktop */}
            <div className="lg:flex-1">
              {/* Map */}
              <div
                className="rounded-lg overflow-hidden border border-white/10 relative mx-auto w-full md:w-1/2 lg:w-full"
                style={{ aspectRatio: "1 / 1", minHeight: "160px" }}
              >
                {/* Valid-time overlay */}
                {!loading && !loadError && selectedRun && (
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      background: "rgba(0,0,0,0.6)",
                      color: "white",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}
                  >
                    Valid: {formatValidTime(selectedRun, selectedFH)}
                  </div>
                )}
                {loading && (
                  <div className="flex items-center justify-center h-full bg-[#111827]">
                    <div className="text-center space-y-3">
                      <div className="w-8 h-8 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-sm text-[#9CA3AF]">Loading LAI data…</p>
                    </div>
                  </div>
                )}

                {loadError && !loading && (
                  <div className="flex items-center justify-center h-full bg-[#111827]">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-red-400">Run data unavailable</p>
                      <p className="text-xs text-[#6B7280]">
                        Please select a different run or check back later.
                      </p>
                    </div>
                  </div>
                )}

                {!loading && !loadError && (
                  <MapContainer
                    center={MAP_CENTER}
                    zoom={MAP_ZOOM}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl
                  >
                    <MapResizer bounds={UK_BOUNDS} />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    />
                    {filteredData && filteredData.features.length > 0 && (
                      <GeoJSON
                        key={geoJsonKey}
                        ref={geoJsonRef}
                        data={filteredData}
                        style={layerStyle}
                        onEachFeature={onEachFeature}
                      />
                    )}
                  </MapContainer>
                )}
              </div>

              {/* Legend — below the map on both mobile and desktop */}
              <div className="mt-4 mb-4 flex flex-col items-center gap-2">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  LAI.v1 Scale
                </h3>
                <div className="flex w-full max-w-md">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <div key={n} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full h-4 border border-white/10"
                        style={{ backgroundColor: LAI_COLORS[n] }}
                      />
                      <span className="text-[10px] font-mono text-[#6B7280]">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column (desktop only): latest run info + scrollable 3-hourly
                forecast steps. Hidden on mobile — those controls appear in the
                header section above. Only shown at the lg breakpoint and above. */}
            <div className="hidden lg:flex lg:flex-col lg:w-72 xl:w-80 gap-3 lg:sticky lg:top-4">
              {/* Latest run info + run selector */}
              <div className="bg-[#111827] border border-white/10 rounded-lg px-4 py-3 flex flex-col gap-2">
                <span className="text-xs text-[#9CA3AF]">
                  {runIndex && selectedRun && selectedRun !== runIndex.latest
                    ? "Showing run: "
                    : "Showing latest run: "}
                  <span className="font-mono font-semibold text-[#FBBF24]">
                    {selectedRun ? formatRunLabel(selectedRun) : "—"}
                  </span>
                </span>

                {runIndex && runIndex.runs && runIndex.runs.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="run-select-desktop"
                      className="text-xs text-[#6B7280] whitespace-nowrap"
                    >
                      View previous runs
                    </label>
                    <select
                      id="run-select-desktop"
                      value={selectedRun ?? ""}
                      onChange={(e) => handleRunChange(e.target.value)}
                      className="bg-[#1a1a1a] border border-white/10 text-[#F9FAFB] text-xs rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#FBBF24]"
                    >
                      {[...runIndex.runs].reverse().map((run) => (
                        <option key={run} value={run}>
                          {formatRunLabel(run)}
                          {run === runIndex.latest ? " (latest)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Scrollable 3-hourly forecast steps — clickable buttons that
                  update the map to the selected forecast hour */}
              <div className="bg-[#111827] border border-white/10 rounded-lg p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-widest text-[#9CA3AF]">
                    Forecast Steps
                  </span>
                  <span className="font-mono text-sm font-semibold text-[#FBBF24]">
                    T+{selectedFH}
                  </span>
                </div>
                {/* Slider for quick forecast-hour navigation */}
                <input
                  type="range"
                  min={0}
                  max={FORECAST_HOURS.length - 1}
                  step={1}
                  value={FORECAST_HOURS.indexOf(selectedFH)}
                  onChange={(e) => setSelectedFH(FORECAST_HOURS[Number(e.target.value)])}
                  className="w-full accent-yellow-400 cursor-pointer"
                  aria-label="Forecast hour selector"
                />
                {/* Scrollable list of clickable forecast step buttons */}
                <div className="overflow-y-auto max-h-96 space-y-0.5 pr-0.5">
                  {FORECAST_HOURS.map((fh) => (
                    <button
                      key={fh}
                      onClick={() => setSelectedFH(fh)}
                      className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
                        fh === selectedFH
                          ? "bg-[#FBBF24]/20 text-[#FBBF24] font-bold"
                          : "text-[#6B7280] hover:bg-white/5 hover:text-[#9CA3AF]"
                      }`}
                    >
                      <span className="font-mono font-semibold inline-block w-10">T+{fh}</span>
                      <span className="font-sans font-normal text-[10px] ml-1 opacity-80">
                        {selectedRun ? formatValidTime(selectedRun, fh) : ""}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Info panel */}
          <div className="bg-[#111827] border border-white/10 rounded-lg p-5 mb-16">
            <h2 className="font-heading text-xs font-bold uppercase tracking-widest text-[#9CA3AF] mb-3">
              About LAI.v1
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-[#6B7280] leading-relaxed">
              <div>
                <p className="mb-2">
                  The <strong className="text-[#9CA3AF]">Lightning Activity Index (LAI.v1)</strong> is a
                  composite metric derived from three physical components, each scored 0–4:
                </p>
                <ul className="space-y-1 mb-2">
                  <li>· <strong className="text-[#9CA3AF]">Electrification Potential</strong> — CAPE thresholds + PWAT &amp; lapse-rate boosts</li>
                  <li>· <strong className="text-[#9CA3AF]">Convective Intensity</strong> — MLCAPE thresholds</li>
                  <li>· <strong className="text-[#9CA3AF]">Storm Organisation</strong> — 0–6 km bulk shear + SRH boost + CAPE–shear fix</li>
                </ul>
                <p className="font-mono mt-1 text-[#9CA3AF] bg-[#1a1a1a] px-2 py-1 rounded">
                  LAI = (E×1.5 + I×1.2 + O×1.0) / 13 × 10 × limiters
                </p>
                <p className="mt-1 text-[#6B7280]">
                  Limiters: CIN (convective inhibition), freezing-level depth,
                  convective precipitation trigger, and seasonal adjustment reduce
                  LAI in capped, stable, or cold-season conditions.
                </p>
              </div>
              <div>
                <p className="mb-2">
                  Forecasts are produced by blending four NWP models:
                </p>
                <ul className="space-y-1 mb-2">
                  <li>· ECMWF Open Data — 30 %</li>
                  <li>· DWD ICON-EU — 30 %</li>
                  <li>· NOAA GFS 0.25° — 20 %</li>
                  <li>· Met Office UKV — 20 %</li>
                </ul>
                <p>
                  UKV is used where available for short-range, high-resolution convective
                  detail. Missing UKV steps are handled by proportionally redistributing
                  weights to the other three models. A Gaussian smoothing filter (σ = 1.2)
                  is applied before vectorisation. The pipeline runs every 6 hours at 02,
                  08, 14, and 20 UTC and covers T+0 through T+48 in 3-hour steps.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* LAI 24 Hour Forecast tab content                                   */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === "24h" && (
        <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
          <LAI24HForecastTab />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Forecast Animation (GIF) tab content                               */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === "animation" && (
        <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
          <LAIForecastAnimationTab />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Live Lightning tab content                                          */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === "lightning" && (
        <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
          <LiveLightningTab />
        </div>
      )}
    </div>
  );
}
